import type { Answer } from '@coorum-research/core/src/components/stamp-rallies/GameCollectionShelfPhoto'
import type { RefObject } from 'react'
import { MyPageStampRally } from '@coorum-research/core/src/components/MyPageStampRally'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { gameStampRallyApi } from '../apis/game-stamp-rally.api'
import { calcMaxCount, calcTotalCount, calcTypeCount, makeShelfData } from '../utils/game-stamp-rally.util'

const { getGameStampRallies, getGameStampRally } = gameStampRallyApi()

type Props = {
  containerRef: RefObject<HTMLDivElement | null>
  handleAnswerClick: (answerId: string) => void
}

export function MyPageStampRallyContainer(props: Props) {
  const { containerRef, handleAnswerClick } = props

  const [gameStampRallyId, setGameStampRallyId] = useState<string | null>(null)

  const [isImageModalOpen, setIsImageModalOpen] = useState<boolean>(false)
  const [modalAnswer, setModalAnswer] = useState<Answer | null>(null)

  const handleShelfPhotoClick = useCallback((answer: Answer) => {
    setModalAnswer(answer)
    setIsImageModalOpen(true)
  }, [])

  const { data: gameStampRallies } = useQuery({
    queryKey: ['get-game-stamp-rallies'],
    queryFn: () => getGameStampRallies(),
    refetchOnWindowFocus: false,
  })

  const { data: gameStampRally } = useQuery({
    queryKey: ['get-game-stamp-rally', gameStampRallyId],
    queryFn: () => {
      if (gameStampRallyId) {
        return getGameStampRally(gameStampRallyId)
      }
      return null
    },
    enabled: gameStampRallies && gameStampRallies.length > 0 && !!gameStampRallyId,
    refetchOnWindowFocus: false,
  })

  const handleGameStampRallySelect = useCallback((id: string) => {
    setGameStampRallyId(id)
  }, [])

  useEffect(() => {
    if (gameStampRallies && gameStampRallies.length > 0) {
      handleGameStampRallySelect(gameStampRallies[0].id)
    }
  }, [gameStampRallies, handleGameStampRallySelect])

  const totalCount = useMemo(() => {
    if (gameStampRally) {
      return calcTotalCount(gameStampRally.campaigns)
    }
    return 0
  }, [gameStampRally])

  const typeCount = useMemo(() => {
    if (gameStampRally) {
      return calcTypeCount(gameStampRally.campaigns)
    }
    return 0
  }, [gameStampRally])

  const maxCount = useMemo(() => {
    if (gameStampRally) {
      return calcMaxCount(gameStampRally.campaigns)
    }
    return 0
  }, [gameStampRally])

  const shelfData = useMemo(() => {
    if (gameStampRally) {
      return makeShelfData(gameStampRally.campaigns)
    }
    return []
  }, [gameStampRally])

  if (!gameStampRallies) {
    return null
  }

  return (
    <MyPageStampRally
      containerRef={containerRef}
      activeStampRallyId={gameStampRally?.id}
      stampRallies={gameStampRallies}
      setActiveStampRallyId={handleGameStampRallySelect}
      headingContent={gameStampRally?.configJson.headingContent}
      headingTextColor={gameStampRally?.configJson.headingTextColor}
      headingBgImageTemplate={gameStampRally?.configJson.headingBgImageTemplate}
      headingBgImageUrl={gameStampRally?.configJson.headingBgImageUrl}
      totalCount={totalCount}
      typeCount={typeCount}
      maxCount={maxCount}
      recordBlockBgColor={gameStampRally?.configJson.recordBlockBgColor}
      recordBlockTextColor={gameStampRally?.configJson.recordBlockTextColor}
      badgeBgColor={gameStampRally?.configJson.badgeBgColor}
      badgeTextColor={gameStampRally?.configJson.badgeTextColor}
      shelfRowImageTemplate={gameStampRally?.configJson.shelfRowImageTemplate}
      shelfRowImageUrl={gameStampRally?.configJson.shelfRowImageUrl}
      bgImageTemplate={gameStampRally?.configJson.bgImageTemplate}
      bgImageUrl={gameStampRally?.configJson.bgImageUrl}
      shelfData={shelfData}
      columnCount={gameStampRally?.configJson.columnCount}
      handleAnswerClick={handleAnswerClick}
      isImageModalOpen={isImageModalOpen}
      setIsImageModalOpen={setIsImageModalOpen}
      modalAnswer={modalAnswer}
      handleShelfPhotoClick={handleShelfPhotoClick}
    />
  )
}
